"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback
} from "react";
import { User as FirebaseUser, onAuthStateChanged } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { User } from "@/types/user";

interface AuthContextType {
  currentUser: (FirebaseUser & Partial<User>) | null;
  loading: boolean;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<
    (FirebaseUser & Partial<User>) | null
  >(null);
  const [loading, setLoading] = useState(true);

  // Function to fetch user data from Firestore
  const fetchUserData = useCallback(
    async (uid: string): Promise<Partial<User>> => {
      try {
        const userDoc = await getDoc(doc(db, "users", uid));
        if (userDoc.exists()) {
          return userDoc.data() as Partial<User>;
        }
      } catch (error) {
        console.error("Error fetching user data from Firestore:", error);
      }
      return {};
    },
    []
  );

  // Function to merge Firebase Auth user with Firestore data
  const mergeUserData = useCallback(
    (authUser: FirebaseUser, firestoreData: Partial<User>) => {
      return {
        ...authUser,
        ...firestoreData,
        id: authUser.uid, // Ensure id matches uid
        email: authUser.email ?? firestoreData.email ?? ""
      };
    },
    []
  );

  // Manual refresh function
  const refreshUserData = useCallback(async () => {
    if (currentUser?.uid) {
      const firestoreData = await fetchUserData(currentUser.uid);
      setCurrentUser((prev) =>
        prev ? mergeUserData(prev, firestoreData) : null
      );
    }
  }, [currentUser?.uid, fetchUserData, mergeUserData]);

  useEffect(() => {
    let firestoreUnsub: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(
      auth,
      async (authUser: FirebaseUser | null) => {
        console.log(
          "Auth state changed:",
          authUser ? authUser.email : "No user"
        );

        // Clean up previous Firestore listener
        if (firestoreUnsub) {
          firestoreUnsub();
          firestoreUnsub = null;
        }

        if (authUser) {
          // Fetch initial Firestore data
          const firestoreData = await fetchUserData(authUser.uid);
          const mergedUser = mergeUserData(authUser, firestoreData);
          setCurrentUser(mergedUser);

          // Set up real-time listener for Firestore user document
          const userDocRef = doc(db, "users", authUser.uid);
          firestoreUnsub = onSnapshot(
            userDocRef,
            (docSnapshot) => {
              if (docSnapshot.exists()) {
                const updatedData = docSnapshot.data() as Partial<User>;
                console.log("User document updated:", updatedData);
                setCurrentUser(mergeUserData(authUser, updatedData));
              }
            },
            (error) => {
              console.error("Error listening to user document:", error);
            }
          );
        } else {
          setCurrentUser(null);
        }

        setLoading(false);
      }
    );

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeAuth();
      if (firestoreUnsub) {
        firestoreUnsub();
      }
    };
  }, [fetchUserData, mergeUserData]);

  const value = useMemo(
    () => ({ currentUser, loading, refreshUserData }),
    [currentUser, loading, refreshUserData]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
